import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  api,
  setTokens,
  clearTokens,
  getToken,
  getRefreshToken,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      clearTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens(data.access_token, data.refresh_token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    window.dispatchEvent(new CustomEvent("simpler:auth-changed", { detail: { type: "login" } }));
    return me.data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setTokens(data.access_token, data.refresh_token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    window.dispatchEvent(new CustomEvent("simpler:auth-changed", { detail: { type: "register" } }));
    return me.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {
        refresh_token: getRefreshToken() || "",
      });
    } catch { /* ignore */ }
    clearTokens();
    setUser(null);
    window.dispatchEvent(new CustomEvent("simpler:auth-changed", { detail: { type: "logout" } }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, reload: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
